import apiClient from './client'

export interface SourceMap {
  id: string
  app_id: string
  release: string
  filename: string
  original_filename: string | null
  file_size_bytes: number | null
  created_at: string
}

export interface SourceMapListResponse {
  source_maps: SourceMap[]
  total: number
}

export interface SourceMapUploadRequest {
  release: string
  filename: string
  original_filename?: string
  content_type?: string
}

export interface SourceMapUploadResponse {
  upload_url: string
  fields: Record<string, string>
  source_map_id: string
  s3_key: string
  max_size: number
}

export interface SourceMapConfirmRequest {
  source_map_id: string
  file_size_bytes?: number
}

export interface SourceMapDeleteResponse {
  deleted: boolean
  message: string
}

export const sourceMapsApi = {
  /**
   * List all source maps for an app
   */
  list: async (appId: string, release?: string): Promise<SourceMapListResponse> => {
    const params = release ? { release } : {}
    const response = await apiClient.get<SourceMapListResponse>(`/apps/${appId}/source-maps`, {
      params,
    })
    return response.data
  },

  /**
   * List all releases that have source maps
   */
  listReleases: async (appId: string): Promise<string[]> => {
    const response = await apiClient.get<string[]>(`/apps/${appId}/source-maps/releases`)
    return response.data
  },

  /**
   * Get a presigned URL for uploading a source map
   */
  getUploadUrl: async (
    appId: string,
    data: SourceMapUploadRequest
  ): Promise<SourceMapUploadResponse> => {
    const response = await apiClient.post<SourceMapUploadResponse>(
      `/apps/${appId}/source-maps/upload-url`,
      data
    )
    return response.data
  },

  /**
   * Confirm that a source map upload completed
   */
  confirmUpload: async (appId: string, data: SourceMapConfirmRequest): Promise<SourceMap> => {
    const response = await apiClient.post<SourceMap>(`/apps/${appId}/source-maps/confirm`, data)
    return response.data
  },

  /**
   * Delete a specific source map
   */
  delete: async (appId: string, sourceMapId: string): Promise<SourceMapDeleteResponse> => {
    const response = await apiClient.delete<SourceMapDeleteResponse>(
      `/apps/${appId}/source-maps/${sourceMapId}`
    )
    return response.data
  },

  /**
   * Delete all source maps for a release
   */
  deleteByRelease: async (appId: string, release: string): Promise<SourceMapDeleteResponse> => {
    const response = await apiClient.delete<SourceMapDeleteResponse>(`/apps/${appId}/source-maps`, {
      params: { release },
    })
    return response.data
  },

  /**
   * Upload a source map file
   * This is a two-step process:
   * 1. Get a presigned URL
   * 2. Upload the file directly to S3
   * 3. Confirm the upload
   */
  upload: async (
    appId: string,
    file: File,
    release: string,
    onProgress?: (percent: number) => void
  ): Promise<SourceMap> => {
    // Step 1: Get presigned URL
    const uploadInfo = await sourceMapsApi.getUploadUrl(appId, {
      release,
      filename: file.name,
      original_filename: file.name.replace(/\.map$/, ''),
      content_type: file.type || 'application/json',
    })

    // Step 2: Upload to S3
    const formData = new FormData()
    Object.entries(uploadInfo.fields).forEach(([key, value]) => {
      formData.append(key, value)
    })
    formData.append('file', file)

    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhr.open('POST', uploadInfo.upload_url, true)

      if (onProgress) {
        xhr.upload.onprogress = event => {
          if (event.lengthComputable) {
            onProgress(Math.round((event.loaded / event.total) * 100))
          }
        }
      }

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve()
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`))
        }
      }
      xhr.onerror = () => reject(new Error('Upload failed'))
      xhr.send(formData)
    })

    // Step 3: Confirm upload
    const sourceMap = await sourceMapsApi.confirmUpload(appId, {
      source_map_id: uploadInfo.source_map_id,
      file_size_bytes: file.size,
    })

    return sourceMap
  },
}
